import { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2 } from "lucide-react";
import { Company } from "@/types";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { companyAtom } from "@/store/company";
import { useAtom } from "jotai";

export default function CompanyInfo(): ReactElement {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [company] = useAtom(companyAtom);

  // Fetch detailed company information
  const {
    data: companyDetails,
    isLoading: isLoadingCompany,
    error,
  } = useQuery<Company>({
    queryKey: ["company", company?.id],
    enabled: !!company?.id,
    queryFn: async () => {
      const response = await fetch(getApiUrl(`companies/${company?.id}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch company details");
      }
      return response.json();
    },
  });

  if (error) {
    return (
      <div className="text-destructive">Error: {(error as Error).message}</div>
    );
  }

  return (
    <div>
      {/* Company Info Card */}
      {company?.id && (
        <div className="mt-6 mb-4">
          <Card className="border-muted bg-card">
            <CardHeader className="pb-2">
              <div className="flex items-center">
                <div className="mr-4 h-12 w-12 rounded-full overflow-hidden bg-accent flex items-center justify-center">
                  {companyDetails?.logo ? (
                    <img
                      src={companyDetails.logo}
                      alt={companyDetails.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold">
                    {companyDetails?.name || company?.name || ""}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {companyDetails?.vat || "-"}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pb-2 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <div>
                    <p className="text-sm">
                      <span className="font-medium">
                        {t("companies.fields.sector")}:{" "}
                      </span>
                      <span className="text-muted-foreground">
                        {companyDetails?.sector?.name || "-"}
                      </span>
                    </p>
                  </div>
                  <p className="text-sm">
                    <span className="font-medium">
                      {t("companies.fields.country")}:{" "}
                    </span>
                    <span className="text-muted-foreground">
                      {companyDetails?.country?.name || "-"}
                    </span>
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">
                      {t("companies.fields.province")}:{" "}
                    </span>
                    <span className="text-muted-foreground">
                      {companyDetails?.province || "-"}
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
            {isLoadingCompany && (
              <div className="p-4">
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
